import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { Card } from '@design/components/card/Card';
import { Icon } from '@design/components/icon/Icon';
import {
  type AppTableFeatures,
  appTableFeatures,
} from '@design/components/table';
import { ColumnDef, useTable } from '@tanstack/react-table';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { EarningsDrawer } from '@terminal/views/theme-overview/tab-content/earnings/components/earnings-drawer/EarningsDrawer';
import { DateCell } from '@terminal/widgets/earnings/components/date-cell/DateCell';
import { PriceCell } from '@terminal/widgets/earnings/components/price-cell/PriceCell';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ThemeEarningsTable } from '../theme-earnings-table/ThemeEarningsTable';
import { useThemeEarningsDrawer } from '../useThemeEarningsDrawer';
import { useThemeEarningsInsights } from '../useThemeEarningsInsights';
import { useThemeEarningsThemes } from '../useThemeEarningsThemes';
import type { EarningsPresentationVariant } from '../utils';
import { EarningsCompanyCard } from './EarningsCompanyCard';
import styles from './EarningsReportingSummary.module.scss';
import { ReportingSummaryEmptyState } from './ReportingSummaryEmptyState';
import { REPORTING_THIS_WEEK_COLUMN_LAYOUT } from './reportingThisWeekColumnLayout';
import { TopicsCell } from './TopicsCell';
import type { EarningsTopicClickHandler, ReportingThisWeekRow } from './types';

const COLLAPSED_ROW_COUNT = 3;
const TOP_THEMES_TO_WATCH_COUNT = 5;

interface ReportingThisWeekCardProps {
  rows: ReportingThisWeekRow[];
  onTopicClick: EarningsTopicClickHandler;
  variant?: EarningsPresentationVariant;
}

export const ReportingThisWeekCard = ({
  rows,
  onTopicClick,
  variant = 'page',
}: ReportingThisWeekCardProps) => {
  const { t } = useTranslation(['widget']);
  const isPage = variant === 'page';
  const [isExpanded, setIsExpanded] = useState(false);
  const { earningsInsightByEarningsId, isKgFilterLoading } =
    useThemeEarningsInsights({
      earningsIds: rows.map(earning => earning.id),
      isEnabled: isPage,
    });

  const { themesByEarningsId } = useThemeEarningsThemes(
    earningsInsightByEarningsId,
    'preview',
    isPage && !isKgFilterLoading
  );

  const enrichedRows = useMemo(
    () =>
      rows.map(row => ({
        ...row,
        themesToWatch: (
          themesByEarningsId.get(row.id) ??
          row.themesToWatch ??
          []
        ).slice(0, TOP_THEMES_TO_WATCH_COUNT),
      })),
    [rows, themesByEarningsId]
  );
  const { drawerData, onRowClick, onDrawerClose } = useThemeEarningsDrawer(
    earningsInsightByEarningsId
  );

  const canExpand = enrichedRows.length > COLLAPSED_ROW_COUNT;

  const visibleRows = useMemo(
    () =>
      isExpanded || !canExpand
        ? enrichedRows
        : enrichedRows.slice(0, COLLAPSED_ROW_COUNT),
    [canExpand, isExpanded, enrichedRows]
  );

  const columns: ColumnDef<AppTableFeatures, ReportingThisWeekRow>[] = [
    {
      id: 'rank',
      header: () => t('widget:earnings.rank'),
      cell: ({ row }) => <TopRankIcon rank={row.original.rank} />,
    },
    {
      id: 'company',
      header: () => t('widget:earnings.company'),
      cell: ({ row }) => <EntityCell entityTag={row.original.entity_tag} />,
    },
    {
      id: 'scheduled-date',
      header: () => (
        <span className={styles.scheduledDateHeader}>
          {t('widget:earnings.scheduledDate')}
          <Icon
            iconName="SortAscLeft"
            size={12}
            fillColor="var(--icon-primary)"
            data-testid="scheduled-date-sort-icon"
          />
        </span>
      ),
      cell: ({ row }) => (
        <DateCell date={new Date(row.original.reporting_date)} />
      ),
    },
    {
      id: 'expected-eps',
      header: () =>
        t('widget:themeOverview.tabs.earnings.reportingSummary.expectedEps'),
      cell: ({ row }) =>
        row.original.eps.estimate !== undefined
          ? row.original.eps.estimate.toFixed(2)
          : '-',
    },
    {
      id: 'expected-revenue',
      header: () =>
        t(
          'widget:themeOverview.tabs.earnings.reportingSummary.expectedRevenue'
        ),
      cell: ({ row }) => <PriceCell value={row.original.revenue.estimate} />,
    },
    {
      id: 'themes-to-watch',
      header: () =>
        t(
          'widget:themeOverview.tabs.earnings.reportingSummary.topThemesToWatch'
        ),
      cell: ({ row }) => (
        <TopicsCell
          topics={row.original.themesToWatch ?? []}
          onTopicClick={onTopicClick}
        />
      ),
    },
  ];

  const table = useTable({
    features: appTableFeatures,
    data: visibleRows,
    columns,
    getRowId: row => row.id,
    enableSorting: false,
    initialState: {
      pagination: { pageIndex: 0, pageSize: Infinity },
    },
  });

  if (rows.length === 0) {
    return (
      <ReportingSummaryEmptyState
        title={t(
          'widget:themeOverview.tabs.earnings.reportingSummary.reportingThisWeek'
        )}
        message={t(
          'widget:themeOverview.tabs.earnings.reportingSummary.noCompaniesReportingThisWeek'
        )}
        dataTestId="reporting-this-week-card"
      />
    );
  }

  if (variant === 'drawer') {
    return (
      <section
        className={styles.drawerSection}
        data-testid="reporting-this-week-card"
      >
        <h3 className={styles.drawerTitle}>
          {t(
            'widget:themeOverview.tabs.earnings.reportingSummary.reportingThisWeek'
          )}
        </h3>
        <div className={styles.companyCardGrid}>
          {enrichedRows.map(row => (
            <EarningsCompanyCard key={row.id} row={row} variant="upcoming" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <Card
      size="large"
      className={canExpand ? styles.cardWithFooter : styles.card}
      data-testid="reporting-this-week-card"
    >
      <EarningsDrawer drawerData={drawerData} onDrawerClose={onDrawerClose} />
      <p className={styles.title}>
        {t(
          'widget:themeOverview.tabs.earnings.reportingSummary.reportingThisWeek'
        )}
      </p>
      <div className={styles.weekContent}>
        <div className={styles.tableWrapper}>
          <ThemeEarningsTable
            table={table}
            columnLayout={REPORTING_THIS_WEEK_COLUMN_LAYOUT}
            onRowClick={onRowClick}
            dataTestId="reporting-this-week-table"
          />
        </div>
        {canExpand && (
          <div className={styles.footer}>
            <ButtonLink
              iconName={isExpanded ? 'ChevronLightUp' : 'ChevronLightDown'}
              iconSize={16}
              label={t(
                isExpanded
                  ? 'widget:themeOverview.tabs.earnings.reportingSummary.viewLess'
                  : 'widget:themeOverview.tabs.earnings.reportingSummary.viewAllCompanies'
              )}
              onClick={() => setIsExpanded(expanded => !expanded)}
              data-testid="view-all-reporting-companies"
            />
          </div>
        )}
      </div>
    </Card>
  );
};
